<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import FollowUpStepCard from './FollowUpStepCard.vue';
import FollowUpStepEditor from './FollowUpStepEditor.vue';
import { cumulativeDay } from './followUpTime';

const props = defineProps({
  stageKey: {
    type: String,
    required: true,
  },
  stageLabel: {
    type: String,
    default: '',
  },
  stageColor: {
    type: String,
    default: '',
  },
  steps: {
    type: Array,
    default: () => [],
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  templates: {
    type: Array,
    default: () => [],
  },
  templatesByInbox: {
    type: Object,
    default: null,
  },
  // Vozes oferecidas ao passo "Áudio da IA" — repassado sem alteração ao
  // FollowUpStepEditor (ver props lá para o formato).
  voices: {
    type: Array,
    default: () => [],
  },
  // Destinos do passo "Mover card" (ver FollowUpStepEditor).
  funnels: {
    type: Array,
    default: () => [],
  },
  funnelId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['update:steps']);

const { t } = useI18n();

// Defaults do passo novo pedidos no brief: texto (nunca template, senão o
// select do editor trava o Save até o usuário interagir) e 24h de espera.
const newStepDraft = () => ({
  delay_hours: 24,
  message_type: 'text',
  content: '',
  inbox_id: null,
  paused: false,
});

const generateStepId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `step-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Tinge os nós da linha do tempo com a cor da própria etapa do funil,
// amarrando visualmente a timeline ao pontinho colorido do acordeão em
// FunnelSettings. Sem cor definida, cai no n-brand default das classes.
const nodeAccentStyle = computed(() =>
  props.stageColor
    ? { borderColor: props.stageColor, color: props.stageColor }
    : undefined
);

// Delay negativo digitado à mão passa do min=0 do input — sempre clampado
// aqui antes de persistir (gotcha herdado do review da Task 3).
const clampDelay = step => ({
  ...step,
  delay_hours: Math.max(0, Number(step.delay_hours) || 0),
});

// Só um passo por etapa pode ser o "na hora": dois disparariam juntos no mesmo
// segundo quando o lead entra, que é cara de bot. Marcar um desmarca o anterior.
const enforceSingleInstant = (steps, instantIndex) =>
  steps.map((step, i) =>
    i === instantIndex ? step : { ...step, on_stage_enter: false }
  );

const expandedIndex = ref(null);
const isAddingStep = ref(false);
const draftStep = ref(newStepDraft());
const pendingRemoveIndex = ref(null);
const removeDialogRef = ref(null);

const localSteps = computed({
  get: () => props.steps,
  set: value => emit('update:steps', value),
});

const cumulativeHoursList = computed(() => {
  let running = 0;
  return props.steps.map(step => {
    running += Math.max(0, Number(step.delay_hours) || 0);
    return running;
  });
});

const dayLabel = index =>
  t('FUNNEL_TIMELINE.DAY', {
    n: cumulativeDay(cumulativeHoursList.value[index] || 0),
  });

const stepKey = step => step.id ?? `idx-${props.steps.indexOf(step)}`;

const toggleExpand = index => {
  expandedIndex.value = expandedIndex.value === index ? null : index;
  isAddingStep.value = false;
};

const togglePause = index => {
  const next = props.steps.map((step, i) =>
    i === index ? { ...step, paused: !step.paused } : step
  );
  emit('update:steps', next);
};

const duplicateStep = index => {
  const original = props.steps[index];
  // A cópia nunca herda paused nem o disparo imediato — este último é exclusivo
  // da etapa, e duplicá-lo criaria dois "na hora".
  const { paused, on_stage_enter: onStageEnter, ...rest } = original;
  const duplicated = { ...rest, id: generateStepId() };

  const next = [...props.steps];
  next.splice(index + 1, 0, duplicated);

  expandedIndex.value = null;
  emit('update:steps', next);
};

const requestRemove = index => {
  pendingRemoveIndex.value = index;
  removeDialogRef.value?.open();
};

const confirmRemove = () => {
  if (pendingRemoveIndex.value === null) return;

  const next = props.steps.filter((_, i) => i !== pendingRemoveIndex.value);
  pendingRemoveIndex.value = null;
  expandedIndex.value = null;
  removeDialogRef.value?.close();
  emit('update:steps', next);
};

const saveStep = (index, payload) => {
  let next = props.steps.map((step, i) =>
    i === index ? clampDelay(payload) : step
  );
  if (payload.on_stage_enter) next = enforceSingleInstant(next, index);
  expandedIndex.value = null;
  emit('update:steps', next);
};

const cancelEdit = () => {
  expandedIndex.value = null;
};

const startAdd = () => {
  draftStep.value = newStepDraft();
  expandedIndex.value = null;
  isAddingStep.value = true;
};

const saveNewStep = payload => {
  const step = clampDelay({ ...payload, id: generateStepId() });
  let next = [...props.steps, step];
  if (step.on_stage_enter) next = enforceSingleInstant(next, next.length - 1);
  emit('update:steps', next);
  isAddingStep.value = false;
};

const cancelAdd = () => {
  isAddingStep.value = false;
};

const onDragEnd = () => {
  expandedIndex.value = null;
};
</script>

<template>
  <div
    :data-testid="`follow-up-timeline-${stageKey}`"
    :aria-label="stageLabel"
    class="space-y-4"
  >
    <div
      v-if="!steps.length && !isAddingStep"
      data-testid="follow-up-timeline-empty"
      class="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-n-weak bg-n-solid-2 px-6 py-10 text-center"
    >
      <div
        class="flex size-12 items-center justify-center rounded-full bg-n-brand/10"
      >
        <span class="i-lucide-sparkles size-5 text-n-brand" />
      </div>
      <div>
        <p class="text-sm font-semibold text-n-slate-12">
          {{ t('FUNNEL_TIMELINE.EMPTY_TITLE') }}
        </p>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('FUNNEL_TIMELINE.EMPTY_HINT') }}
        </p>
      </div>
      <Button
        variant="solid"
        color="blue"
        size="sm"
        icon="i-lucide-plus"
        :label="t('FUNNEL_TIMELINE.ADD_STEP')"
        data-testid="follow-up-timeline-empty-add"
        @click="startAdd"
      />
    </div>

    <div v-else class="relative">
      <div
        class="absolute bottom-0 left-4 top-0 w-px bg-gradient-to-b from-n-brand/60 via-n-brand/20 to-transparent"
      />

      <Draggable
        v-model="localSteps"
        :item-key="stepKey"
        tag="div"
        class="space-y-4"
        ghost-class="follow-up-step-ghost"
        handle=".follow-up-step-drag-handle"
        @end="onDragEnd"
      >
        <template #item="{ element: step, index }">
          <div class="relative pl-12">
            <span
              :data-testid="`follow-up-timeline-node-${index}`"
              :style="nodeAccentStyle"
              class="follow-up-step-drag-handle absolute left-0 top-1 flex size-8 cursor-grab items-center justify-center rounded-full border-2 border-n-brand/50 bg-n-solid-1 text-center text-[10px] font-bold leading-tight text-n-brand"
            >
              <span v-if="step.on_stage_enter" class="i-lucide-zap size-3.5" />
              <template v-else>{{ dayLabel(index) }}</template>
            </span>
            <div class="space-y-2">
              <FollowUpStepCard
                :step="step"
                :index="index"
                :inboxes="inboxes"
                :funnels="funnels"
                :funnel-id="funnelId"
                :expanded="expandedIndex === index"
                @toggle-expand="toggleExpand(index)"
                @toggle-pause="togglePause(index)"
                @duplicate="duplicateStep(index)"
                @remove="requestRemove(index)"
              />
              <FollowUpStepEditor
                v-if="expandedIndex === index"
                :step="step"
                :inboxes="inboxes"
                :templates="templates"
                :templates-by-inbox="templatesByInbox"
                :voices="voices"
                :funnels="funnels"
                :funnel-id="funnelId"
                :stage-key="stageKey"
                @save="payload => saveStep(index, payload)"
                @cancel="cancelEdit"
              />
            </div>
          </div>
        </template>
      </Draggable>

      <div class="relative pl-12 pt-4">
        <span
          v-if="!isAddingStep"
          :style="nodeAccentStyle"
          class="absolute left-0 top-4 flex size-8 items-center justify-center rounded-full border-2 border-dashed border-n-brand/40 bg-n-solid-1 text-n-brand"
        >
          <span class="i-lucide-plus size-3.5" />
        </span>
        <FollowUpStepEditor
          v-if="isAddingStep"
          :step="draftStep"
          :inboxes="inboxes"
          :templates="templates"
          :templates-by-inbox="templatesByInbox"
          :voices="voices"
          :funnels="funnels"
          :funnel-id="funnelId"
          :stage-key="stageKey"
          @save="saveNewStep"
          @cancel="cancelAdd"
        />
        <Button
          v-else
          variant="faded"
          color="blue"
          size="sm"
          icon="i-lucide-plus"
          :label="t('FUNNEL_TIMELINE.ADD_STEP')"
          data-testid="follow-up-timeline-add"
          @click="startAdd"
        />
      </div>
    </div>

    <Dialog
      ref="removeDialogRef"
      type="alert"
      :title="t('FUNNEL_TIMELINE.DELETE')"
      :description="t('FUNNEL_TIMELINE.DELETE_CONFIRM')"
      :confirm-button-label="t('FUNNEL_TIMELINE.DELETE')"
      :cancel-button-label="t('FUNNEL_TIMELINE.CANCEL')"
      @confirm="confirmRemove"
    />
  </div>
</template>
